/**
 * angular-admin-oskey-io
 *
 * @license
 * Copyright (c) 2021-2025, OSKEY SAS. All rights reserved.
 *
 * The source code is protected under international copyright law.
 * All rights reserved and protected by the copyright holders.
 *
 * The source code is confidential and only available to authorized individuals with the permission of the copyright holders.
 * If you encounter this source code and do not have permission, please contact the copyright holders and delete this file.
 */

import { Signal, effect, inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { OSKCurrentUserToken } from '@oskey/core';
import { OSKCurrentUser } from '@oskey/core/types';
import { Observable, ReplaySubject, finalize, map, skipWhile, take } from 'rxjs';
import { OSKAuthService } from '../../../features/authentication/services/auth.service';

export const OSKLoggedInGuard: CanActivateFn = (
  route,
  state
): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree => {
  const currentUser: Signal<OSKCurrentUser> = inject(OSKCurrentUserToken);
  const authService: OSKAuthService = inject(OSKAuthService);
  const router: Router = inject(Router);
  /**
   * We use that ReplaySubject instead of toObservable() to prevent leaks from the underlying effect, since the guard's injection context may not be destroyed for a long period of time. See:
   * {@link https://github.com/angular/angular/issues/51280}
   * {@link https://github.com/angular/angular/issues/51290}.
   *
   * This way we can destroy the effect when the authStatus observable completes.
   */
  const authStatus = new ReplaySubject<OSKCurrentUser>(1);

  const watcher = effect(() => {
    authService.isLoading();
    authStatus.next(currentUser());
  });

  return authStatus.pipe(
    skipWhile(
      (user: OSKCurrentUser): boolean =>
        user.isOskVerified === undefined || authService.isLoading()
    ),
    take(1),
    map((user) => {
      if (user.firebaseUser && user.isOskVerified && user.oskUser) {
        return true;
      }
      return router.createUrlTree(['/auth'], {
        queryParams: state.url && state.url !== '/' ? { returnUrl: state.url } : undefined
      });
    }),
    finalize(() => watcher.destroy())
  );
};
